import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import difference from 'lodash/difference';
import uniqBy from 'lodash/uniqBy';
import each from 'lodash/each';
import { Type } from 'class-transformer';

import BaseIvrNode, { EMPTY_NEXT_ID_MESSAGE } from './baseIvrNode';
import IvrNode from 'src/models/Ivrs/IvrNode';
import { getMessageTypeClass, MessageRecording, MessageText } from './message';
import NODE_TYPES from 'src/models/Ivrs/NodeTypes';
import {
  ValidateNested,
  Min,
  Max,
  IsString,
  IsOptional,
  IsNumberString,
  IsNumber,
  IsNotEmpty,
  ArrayNotEmpty,
} from 'class-validator';

const maxNanoSeconds = 6e10; // 60sec
export default class MenuNode extends BaseIvrNode {
  @ValidateNested({ each: true })
  @Type(() => Menu)
  @ArrayNotEmpty({ message: 'Select at least one option' })
  menu: Menu[];

  @IsString()
  @IsOptional()
  language: string;

  @IsNumber()
  @Min(1, { message: 'Value must be a number greater than 0' })
  @Max(maxNanoSeconds, { message: 'Value must be a number less than 60' })
  speechTimeout: number;

  @ValidateNested()
  @Type(getMessageTypeClass)
  message: MessageText | MessageRecording;

  @IsString()
  @IsOptional()
  badSelectionNodeId: string;
}

export class Menu {
  @IsNumberString({}, { message: 'Should be a number' })
  dtmf: string;

  @IsString()
  @IsOptional()
  speech: string;

  @IsString()
  @IsNotEmpty({ message: EMPTY_NEXT_ID_MESSAGE })
  nextId?: string;
}

export function validateMenuNode(node?: IvrNode) {
  if (get(node, 'type') === NODE_TYPES.IVR_NODE_MENU) {
    const menuItems = get(node, 'ivrNode.menu');
    const nodeId = get(node, 'ivrNode.id');
    const nextNodeId = get(node, 'ivrNode.badSelectionNodeId');

    if (isEmpty(menuItems) && nodeId === nextNodeId) {
      return [
        {
          attr: `nodesMap.${nodeId}.ivrNode.badSelectionNodeId`,
          code: 'taken',
          detail: 'The node should not refer to itself. Please choose another node or create a new one.',
        },
      ];
    }
  }

  return [];
}

export function validateMenuOptions(node?: IvrNode) {
  if (get(node, 'type') === NODE_TYPES.IVR_NODE_MENU) {
    const options: Menu[] = get(node, 'ivrNode.menu', []);
    const nodeId = get(node, 'ivrNode.id');

    const diff = difference(
      options,
      uniqBy(options, n => n.dtmf)
    );
    const errors: any[] = [];
    each(diff, (menu: any) => {
      each(options, (item: any, index) => {
        if (item.label && item.label === menu.label) {
          errors.push({
            attr: `nodesMap.${nodeId}.ivrNode.menu[${index}].label`,
            code: 'taken',
            detail: 'Duplicate label. Please use a unique label.',
          });
        }

        if (item.dtmf === menu.dtmf) {
          errors.push({
            attr: `nodesMap.${nodeId}.ivrNode.menu[${index}].dtmf`,
            code: 'taken',
            detail: 'This Dial already exists. Please enter a unique dial',
          });
        }
      });
    });
    return errors;
  }
  return [];
}
